/**
 * BwC Feature Panel Component
 * Displays feature requests and evidence for a program participant in a rich card format
 */

import { Icon, Link, PanelType, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import React from "react";
import CustomPanel from "../../SharedComponents/CustomPanel/CustomPanel";
import { useLazyGetBwCParticipantFeatureRequestsQuery } from "../../slices/BuildWithCustomer.slice";
import { IBwCFeaturePanelProps, IBwCFeatureRequest } from "./BuildWithCustomer.interfaces";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";

/**
 * Format date for display
 */
const formatDate = (dateString: string | null): string => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
};

// Status color mapping for feature request status
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    "Pending Triage": { bg: "#fff4ce", text: "#8a6d3b", border: "#f0c36d" },
    "In Review": { bg: "#cce5ff", text: "#004085", border: "#b8daff" },
    "Approved": { bg: "#d4edda", text: "#155724", border: "#c3e6cb" },
    "Rejected": { bg: "#f8d7da", text: "#721c24", border: "#f5c6cb" },
    "In Progress": { bg: "#d1ecf1", text: "#0c5460", border: "#bee5eb" },
    "Completed": { bg: "#d4edda", text: "#155724", border: "#c3e6cb" },
    "default": { bg: "#e2e3e5", text: "#383d41", border: "#d6d8db" }
};

const getStatusColor = (status: string) => {
    return STATUS_COLORS[status] || STATUS_COLORS["default"];
};

/**
 * Single Feature Card Component with expand/collapse capability
 */
const FeatureCard: React.FC<{ feature: IBwCFeatureRequest; index: number; defaultExpanded?: boolean }> = ({ feature, index }) => {
    const [isDetailsExpanded, setIsDetailsExpanded] = React.useState(true);
    const [isEvidenceExpanded, setIsEvidenceExpanded] = React.useState(true);
    const statusColor = getStatusColor(feature.featureRequestStatus);

    return (
        <div style={{
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #e1dfdd",
            marginBottom: 20,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            overflow: "hidden"
        }}>
            {/* Feature Request Header */}
            <div
                style={{
                    background: "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
                    padding: "16px 20px",
                    color: "#ffffff"
                }}
            >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                            <div style={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                background: "rgba(255,255,255,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700
                            }}>
                                {index + 1}
                            </div>
                            <div style={{
                                background: "rgba(255,255,255,0.15)",
                                padding: "4px 10px",
                                borderRadius: 4,
                                fontSize: 11,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1
                            }}>
                                <span style={{ fontSize: 9, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Feature Request ID</span>
                                <span style={{ fontWeight: 600 }}>{feature.featureRequestId || "N/A"}</span>
                            </div>
                        </div>
                        <Text style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", lineHeight: 1.4 }}>
                            {feature.featureName || "Untitled Feature Request"}
                        </Text>
                    </div>
                    {/* Status */}
                    <div style={{
                        background: statusColor.bg,
                        color: statusColor.text,
                        border: `1px solid ${statusColor.border}`,
                        padding: "5px 10px",
                        borderRadius: 16,
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0
                    }}>
                        {feature.featureRequestStatus || "Unknown"}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div>
                {/* Feature Request Details Section */}
                <div style={{ padding: "16px 20px", background: "#fafafa", borderBottom: "1px solid #edebe9" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            padding: "10px 14px",
                            background: isDetailsExpanded
                                ? "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)"
                                : "linear-gradient(135deg, #fffdf5 0%, #fff8e1 100%)",
                            borderRadius: 8,
                            border: "1px solid #f0e6c8",
                            transition: "all 0.2s ease"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsDetailsExpanded(!isDetailsExpanded);
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: "#f0a500",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}>
                                <Icon iconName="Lightbulb" style={{ fontSize: 16, color: "#ffffff" }} />
                            </div>
                            <Text style={{ fontSize: 13, fontWeight: 600, color: "#856404" }}>
                                Feature Request Details
                            </Text>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            color: "#856404",
                            fontSize: 12,
                            fontWeight: 500
                        }}>
                            {isDetailsExpanded ? "Hide" : "Show"}
                            <Icon
                                iconName={isDetailsExpanded ? "ChevronUp" : "ChevronDown"}
                                style={{ fontSize: 14 }}
                            />
                        </div>
                    </div>

                    {/* Details Content */}
                    <div style={{
                        maxHeight: isDetailsExpanded ? "800px" : "0",
                        overflow: "hidden",
                        transition: "max-height 0.3s ease-in-out"
                    }}>
                        <div style={{ marginTop: 12 }}>
                            {/* Description */}
                            <div style={{ marginBottom: 12 }}>
                                <Text style={{ fontSize: 12, color: "#605e5c", fontWeight: 600, marginBottom: 4, display: "block" }}>
                                    Description
                                </Text>
                                <Text style={{
                                    fontSize: 13,
                                    color: feature.featureDescription ? "#323130" : "#a19f9d",
                                    lineHeight: 1.5,
                                    display: "block",
                                    background: "#ffffff",
                                    padding: 12,
                                    borderRadius: 6,
                                    border: "1px solid #edebe9",
                                    fontStyle: feature.featureDescription ? "normal" : "italic"
                                }}>
                                    {feature.featureDescription || "N/A"}
                                </Text>
                            </div>

                            {/* Notes */}
                            <div style={{ marginBottom: 12 }}>
                                <Text style={{ fontSize: 12, color: "#605e5c", fontWeight: 600, marginBottom: 4, display: "block" }}>
                                    Notes
                                </Text>
                                <Text style={{
                                    fontSize: 13,
                                    color: feature.featureRequestNotes ? "#323130" : "#a19f9d",
                                    lineHeight: 1.5,
                                    display: "block",
                                    background: "#ffffff",
                                    padding: 12,
                                    borderRadius: 6,
                                    border: "1px solid #edebe9",
                                    fontStyle: "italic"
                                }}>
                                    {feature.featureRequestNotes || "N/A"}
                                </Text>
                            </div>

                            {/* Owner & Date */}
                            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <Icon iconName="Contact" style={{ fontSize: 14, color: "#605e5c" }} />
                                    <Text style={{ fontSize: 12, color: "#605e5c" }}>
                                        <strong>Owner:</strong> {feature.featureOwner || "—"}
                                    </Text>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <Icon iconName="Calendar" style={{ fontSize: 14, color: "#605e5c" }} />
                                    <Text style={{ fontSize: 12, color: "#605e5c" }}>
                                        <strong>Created:</strong> {formatDate(feature.featureCreatedDate)}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feature Evidence Section */}
                {feature.featureEvidenceId && (
                    <div style={{ padding: "16px 20px", background: "#ffffff" }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                padding: "10px 14px",
                                background: isEvidenceExpanded
                                    ? "linear-gradient(135deg, #e8f4fd 0%, #d0e8f7 100%)"
                                    : "linear-gradient(135deg, #f0f6ff 0%, #e8f4fd 100%)",
                                borderRadius: 8,
                                border: "1px solid #d0e0f5",
                                transition: "all 0.2s ease"
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEvidenceExpanded(!isEvidenceExpanded);
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: "#0078d4",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <Icon iconName="Documentation" style={{ fontSize: 16, color: "#ffffff" }} />
                                </div>
                                <div>
                                    <Text style={{ fontSize: 13, fontWeight: 600, color: "#0078d4", display: "block" }}>
                                        Feature Evidence
                                    </Text>
                                    <Text style={{ fontSize: 11, color: "#605e5c" }}>
                                        <strong>Evidence ID:</strong> {feature.featureEvidenceId}
                                    </Text>
                                </div>
                            </div>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: "#0078d4",
                                fontSize: 12,
                                fontWeight: 500
                            }}>
                                {isEvidenceExpanded ? "Hide" : "Show"}
                                <Icon
                                    iconName={isEvidenceExpanded ? "ChevronUp" : "ChevronDown"}
                                    style={{ fontSize: 14 }}
                                />
                            </div>
                        </div>

                        {/* Evidence Details */}
                        <div style={{
                            maxHeight: isEvidenceExpanded ? "500px" : "0",
                            overflow: "hidden",
                            transition: "max-height 0.3s ease-in-out"
                        }}>
                            <div style={{
                                marginTop: 12,
                                padding: 16,
                                background: "#f8f9fa",
                                borderRadius: 8,
                                border: "1px solid #e1dfdd"
                            }}>
                                {/* Evidence Name */}
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ fontSize: 12, color: "#605e5c", fontWeight: 600, marginBottom: 4, display: "block" }}>
                                        Evidence Name
                                    </Text>
                                    <Text style={{
                                        fontSize: 13,
                                        color: "#323130",
                                        lineHeight: 1.5,
                                        display: "block"
                                    }}>
                                        {feature.featureEvidenceName || "—"}
                                    </Text>
                                </div>

                                {/* Evidence Owner & Date */}
                                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Icon iconName="Contact" style={{ fontSize: 14, color: "#605e5c" }} />
                                        <Text style={{ fontSize: 12, color: "#605e5c" }}>
                                            <strong>Owner:</strong> {feature.featureEvidenceOwner || "—"}
                                        </Text>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Icon iconName="Calendar" style={{ fontSize: 14, color: "#605e5c" }} />
                                        <Text style={{ fontSize: 12, color: "#605e5c" }}>
                                            <strong>Created:</strong> {formatDate(feature.featureEvidenceCreatedDate)}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Styles for loading and empty states
const styles = {
    loadingContainer: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: 40
    },
    emptyState: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        textAlign: "center" as const
    }
};

/**
 * BwC Feature Panel - Main Component
 */
const BwCFeaturePanel: React.FC<IBwCFeaturePanelProps> = ({
    isOpen,
    onDismiss,
    programParticipantId,
    customerName,
    programName,
    featureEvidenceCount
}) => {
    const [getFeatureRequests, { data, isLoading, error, isFetching }] = useLazyGetBwCParticipantFeatureRequestsQuery();

    React.useEffect(() => {
        if (isOpen && programParticipantId) {
            getFeatureRequests({ programParticipantId }, true);
        }
    }, [isOpen, programParticipantId, getFeatureRequests]);

    const renderContent = () => {
        if (error) {
            return <div style={{ marginTop: 30 }}><DisplayAPIsException error={error} height={200} /></div>;
        }

        if (isLoading || isFetching) {
            return (
                <div style={styles.loadingContainer}>
                    <Spinner size={SpinnerSize.large} label="Loading feature requests..." />
                </div>
            );
        }

        if (!data?.featureRequests || data.featureRequests.length === 0) {
            return (
                <div style={styles.emptyState}>
                    <Icon iconName="ProductList" style={{ fontSize: 48, color: "#c8c6c4", marginBottom: 16 }} />
                    <Text variant="large" style={{ fontWeight: 600 }}>No Feature Requests Found</Text>
                    <Text variant="medium" style={{ color: "#605e5c", marginTop: 8 }}>
                        No feature requests recorded for this engagement.
                    </Text>
                </div>
            );
        }

        return (
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 20 }}>
                {/* Customer & Program Info */}
                <div style={{
                    padding: 16,
                    background: "#f8f8f8",
                    borderRadius: 8,
                    border: "1px solid #edebe9"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Icon iconName="Contact" style={{ fontSize: 14, color: "#0078d4" }} />
                        <Text variant="medium" style={{ fontWeight: 600 }}>{customerName}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="Org" style={{ fontSize: 14, color: "#605e5c" }} />
                        <Text variant="medium" style={{ color: "#605e5c" }}>{programName}</Text>
                    </div>
                </div>

                {/* Stats bar */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #e8f4fd 0%, #f0f6ff 100%)",
                    borderRadius: 8,
                    border: "1px solid #d0e0f5"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="ProductList" style={{ fontSize: 18, color: "#0078d4" }} />
                        <Text variant="large" style={{ fontWeight: 700, color: "#0078d4" }}>{data.featureRequests.length}</Text>
                        <Text variant="medium" style={{ color: "#605e5c" }}>Feature Evidence</Text>
                    </div>
                    {data.kustoLink && (
                        <Link
                            href={data.kustoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 12px",
                                background: "#ffffff",
                                borderRadius: 6,
                                border: "1px solid #d0e0f5",
                                textDecoration: "none",
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0078d4"
                            }}
                        >
                            <Icon iconName="OpenInNewWindow" style={{ fontSize: 12 }} />
                            Explore Kusto
                        </Link>
                    )}
                </div>

                {/* Feature Cards */}
                <div>
                    {data.featureRequests.map((feature, index) => (
                        <FeatureCard key={feature.featureEvidenceId || index} feature={feature} index={index} />
                    ))}
                </div>
            </Stack>
        );
    };

    return (
        <CustomPanel
            enablePanel={isOpen}
            dismissPanel={onDismiss}
            panelTitle={`Feature Requests and Evidences (${featureEvidenceCount})`}
            panelType={PanelType.medium}
            isLoading={isLoading || isFetching}
        >
            {renderContent()}
        </CustomPanel>
    );
};

export default BwCFeaturePanel;
